import { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

interface VersionCheckResult {
  hasUpdate: boolean
  currentVersion: string | null
  latestVersion: string | null
  isChecking: boolean
}

/**
 * Hook to check for application updates by comparing versions
 * Checks on navigation changes (not polling) as per requirements
 *
 * @returns Object with update status and version information
 */
export function useVersionCheck(): VersionCheckResult {
  const [currentVersion, setCurrentVersion] = useState<string | null>(null)
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const location = useLocation()

  // Load initial version on mount
  useEffect(() => {
    const loadInitialVersion = async () => {
      try {
        // Fetch overrides.js as text
        // Using cache-busting query parameter to ensure we get fresh data
        const response = await fetch(`/static/config/overrides.js?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
          },
        })

        const text = await response.text()

        const versionMatch = text.match(/VERSION:\s*['"]([^'"]+)['"]/)

        if (versionMatch && versionMatch[1]) {
          const version = versionMatch[1]

          if (version !== '__VERSION_PLACEHOLDER__') {
            setCurrentVersion(version)
            setLatestVersion(version)
            console.log('[Version Check] Initial version:', version)
          } else {
            console.warn('[Version Check] VERSION not set in overrides.js')
          }
        } else {
          console.warn('[Version Check] Could not parse VERSION from overrides.js')
        }
      } catch (error) {
        console.error('[Version Check] Failed to load initial version:', error)
      }
    }

    loadInitialVersion()
  }, [])

  // Check for updates on navigation
  const checkForUpdate = useCallback(async () => {
    if (!currentVersion || currentVersion === '__VERSION_PLACEHOLDER__') {
      return
    }

    setIsChecking(true)

    try {
      // Fetch the latest version from overrides.js
      // Using cache-busting query param and forcing re-fetch
      const response = await fetch(`/static/config/overrides.js?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      const text = await response.text()

      // Parse the VERSION from the JavaScript module
      // Match: VERSION: 'value' or VERSION: "value"
      const versionMatch = text.match(/VERSION:\s*['"]([^'"]+)['"]/)

      if (versionMatch && versionMatch[1]) {
        const fetchedVersion = versionMatch[1]

        if (fetchedVersion !== '__VERSION_PLACEHOLDER__') {
          setLatestVersion(fetchedVersion)

          if (fetchedVersion !== currentVersion) {
            console.log('[Version Check] Update detected!', `Current: ${currentVersion}, Latest: ${fetchedVersion}`)
          }
        }
      } else {
        console.warn('[Version Check] Could not parse VERSION from overrides.js')
      }
    } catch (error) {
      console.error('[Version Check] Failed to check for updates:', error)
    } finally {
      setIsChecking(false)
    }
  }, [currentVersion])

  // Trigger check on navigation changes
  useEffect(() => {
    if (currentVersion) {
      checkForUpdate()
    }
  }, [location.pathname, checkForUpdate, currentVersion])

  // Determine if there's an update
  const hasUpdate = !!(
    currentVersion &&
    latestVersion &&
    currentVersion !== '__VERSION_PLACEHOLDER__' &&
    latestVersion !== '__VERSION_PLACEHOLDER__' &&
    currentVersion !== latestVersion
  )

  return {
    hasUpdate,
    currentVersion,
    latestVersion,
    isChecking,
  }
}
